import { useEffect, useRef } from 'react';

import { WebSocketEntityType } from '@this+that/models';

import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

export interface EntitySyncHandlers<T> {
  onInsert?: (item: T) => void;
  onModify?: (item: T) => void;
  onRemove?: (item: T) => void;
  onReconnect?: () => void | Promise<void>;
}

/**
 * Subscribes to the INSERT/MODIFY/REMOVE/RECONNECT websocket events for one
 * entity type and forwards each to the matching handler. Handlers are read
 * through a ref, so the subscription is set up once and always calls the
 * latest closures without re-subscribing.
 */
export function useEntitySync<T>(
  entityType: WebSocketEntityType,
  handlers: EntitySyncHandlers<T>,
) {
  const { subscribe } = useWebSocket();
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const unsubscribes = [
      subscribe(entityType, WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE, () =>
        handlersRef.current.onReconnect?.(),
      ),
      subscribe<T>(entityType, WebSocketEventType.INSERT, ({ item }) => {
        if (item) {
          handlersRef.current.onInsert?.(item);
        }
      }),
      subscribe<T>(entityType, WebSocketEventType.MODIFY, ({ item }) => {
        if (item) {
          handlersRef.current.onModify?.(item);
        }
      }),
      subscribe<T>(entityType, WebSocketEventType.REMOVE, ({ item }) => {
        if (item) {
          handlersRef.current.onRemove?.(item);
        }
      }),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [subscribe, entityType]);
}
